import { ReactComponent as PackageIcon } from 'assets/icons/package.svg';
import { Button } from 'components/atoms';
import { Card } from 'components/molecules';
import LabeledDeliveryStatus from 'components/molecules/labeledStatus/LabeledDeliveryStatus';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useCustomParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCaretLeft } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  useCancelDigitalDelivery,
  usePostAllDigitalDeliveries,
  usePutAllDigitalDeliveries,
} from 'services/digital-delivery';
import {
  AsapType,
  DELIVERY_METHOD,
  IDigitalDeliveryBase,
  IDigitalDeliveryForm,
} from 'services/digital-delivery/types';
import {
  useGetMusicProductDigitalDeliveries,
  useGetMusicProductDigitalDeliveryInformation,
} from 'services/music-product-digital/delivery';
import { DELIVERY_STATUS, SUBSCRIPTION_TYPE } from 'services/music-product-digital/types/enums';
import { IMusicProductDigitalForm } from 'services/music-product-digital/types/form';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatDateToInput } from 'utils/global/date.utils';
import { isEmptyId } from 'utils/global/validation.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import DigitalDeliveryForm from '../../digitalDelivery/DigitalDeliveryForm';

const MusicProductDigitalDeliveryAll = () => {
  const productId = useCustomParamId('id', '/music-product-digital');

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { values, setFieldValue }: IFormikContextType<IMusicProductDigitalForm> =
    useFormikContext<IMusicProductDigitalForm>();

  const digitalDelivery = useGetMusicProductDigitalDeliveryInformation(productId);

  const deliveries = useGetMusicProductDigitalDeliveries(productId, true);

  const createDigitalDelivery = usePostAllDigitalDeliveries();
  const updateDigitalDelivery = usePutAllDigitalDeliveries();

  const cancelDigitalDelivery = useCancelDigitalDelivery();

  const onSubmitDelivery = async (actions: PopupActions | null) => {
    if (values.delivery) {
      const delivery = middleware.digitalDelivery.formToQueryAll(values.delivery, productId);

      if (isEmptyId(values.delivery.id)) {
        return await createDigitalDelivery.mutateAsync(delivery, {
          onSuccess: async () => {
            await digitalDelivery.refetch();
            actions?.close();
          },
          onError: async (error) => onErrorShowAllErrorsToast(error),
        });
      }

      return await updateDigitalDelivery.mutateAsync(delivery, {
        onSuccess: async () => {
          await digitalDelivery.refetch();
          actions?.close();
        },
        onError: async (error) => onErrorShowAllErrorsToast(error),
      });
    }
  };

  const onCancelDelivery = async (actions: PopupActions) => {
    await cancelDigitalDelivery.mutateAsync(
      {
        productId,
        salesChannel: null,
        providerId: null,
      },
      {
        onSuccess: async () => {
          await digitalDelivery.refetch();
          actions.close();
        },
        onError: async (error) => onErrorShowAllErrorsToast(error),
      },
    );
  };

  const { minPublishDate, isReleaseDateValid } = useMemo(() => {
    const today = new Date();
    const minDate = new Date();
    minDate.setDate(today.getDate() + 10);

    const isValid = values.releaseDate
      ? new Date(values.releaseDate).getTime() >= minDate.getTime()
      : false;

    return {
      minPublishDate: formatDateToInput(minDate),
      isReleaseDateValid: isValid,
    };
  }, [values.releaseDate]);

  const initialCountry = useMemo<IDigitalDeliveryBase>(
    () => ({
      countryCode: '',
      asap: AsapType.AsapToDate,
      publishDate: minPublishDate,
      publishTime: '00:00',
      endDate: null,
      endTime: null,
      isTimeZoneBased: true,
      subscriptionTypeId: SUBSCRIPTION_TYPE.all,
    }),
    [minPublishDate],
  );

  const initialDigitalDelivery: IDigitalDeliveryForm = {
    id: '00000000-0000-0000-0000-000000000000',
    priority: false,
    salesChannelName: t('music-product-digital.delivery.send-everywhere'),
    salesChannelLogo: null,
    providerName: '',
    deliveryType: 1,
    transferMethod: 0,
    waitForAssetId: false,
    expectTimestamp: false,
    status: DELIVERY_STATUS.notSend,
    included: [],
    excluded: [],
    timestamp: null,
    hasDiscrepancy: false,
    version: 0,
  };

  useEffect(() => {
    if (values.mainTitle.title) {
      let initialDelivery: IDigitalDeliveryForm = digitalDelivery.data?.delivery
        ? middleware.digitalDelivery.queryToForm(digitalDelivery.data?.delivery)
        : initialDigitalDelivery;

      if (initialDelivery.status === DELIVERY_STATUS.notSend) {
        // przypisywanie initial dostawy z backu ale na teraz
        initialDelivery.included = [{ ...initialCountry, countryCode: 'ALL' }];
      }
      setFieldValue('delivery', initialDelivery);
    }
  }, [digitalDelivery.data?.delivery, values.mainTitle.title]);

  return (
    <div className="flex flex-col w-full max-w-[500px] gap-6 pt-12">
      {' '}
      <Button
        fullWidth
        variant={BUTTON_VARIANT.white}
        onClick={() => navigate(`/music-product-digital/${productId}/delivery`)}
      >
        <Button.Icon icon={<PiCaretLeft />} />
        <Button.Text text={t('button.back')} />
      </Button>
      <Card.Delivery
        service={{
          salesChannelId: '',
          salesChannelName: t('music-product-digital.delivery.send-everywhere'),
          logoImageUrl: '',
          providerId: '',
          providerName: (deliveries.data ?? [])[0].providerName ?? '-',
          deliveryMethod: DELIVERY_METHOD.unknown,
          deliveryStatus:
            digitalDelivery.data?.insert || digitalDelivery.data?.delivery
              ? (digitalDelivery.data?.delivery?.status ?? DELIVERY_STATUS.notSend)
              : DELIVERY_STATUS.unavailable,
        }}
        customIcon={
          <PackageIcon height={50} width={50} style={{ fill: 'var(--text)', opacity: 0.5 }} />
        }
      />
      <DigitalDeliveryForm
        onCancelDelivery={onCancelDelivery}
        onSubmitDelivery={onSubmitDelivery}
        initialCountry={initialCountry}
        initialValues={digitalDelivery.data?.delivery ?? initialDigitalDelivery}
        values={values.delivery}
        isReleaseDateValid={isReleaseDateValid}
        minPublishDate={minPublishDate}
      />
    </div>
  );
};

export default MusicProductDigitalDeliveryAll;
